import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { getAuthToken } from '@/lib/auth-token';
import type { Course } from '@/types/courses';

interface UseCoursesOptions {
  courseType?: 'standard' | 'nextgen';
}

export const courseKeys = {
  all: ['courses'] as const,
  byType: (type: string) => [...courseKeys.all, type] as const,
  byId: (id: string) => [...courseKeys.all, 'detail', id] as const,
};

function parseAttachments(raw: unknown) {
  if (!raw) return undefined;
  if (typeof raw === 'string') {
    try {
      return raw.trim() ? JSON.parse(raw) : undefined;
    } catch {
      return undefined;
    }
  }
  return raw;
}

function mapCourseData(data: any[]): Course[] {
  return data.map((course) => ({
    ...course,
    modules: (course.modules || [])
      .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
      .map((m: any) => ({
        ...m,
        lessons: (m.lessons || [])
          .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
          .map((l: any) => ({
            ...l,
            module_id: m.id,
            attachments: parseAttachments(l.attachments),
          })),
      })),
  }));
}

const COURSE_SELECT = `
  id, title, overview, tile_description,
  course_type, is_featured, status,
  skill_domains, lesson_type,
  modules (
    id, title, description, position,
    lessons (
      id, title, content, video_id, position, status, attachments
    )
  )
`;

export function useCourses(options: UseCoursesOptions = {}) {
  const { courseType } = options;

  return useQuery({
    queryKey: courseType ? courseKeys.byType(courseType) : courseKeys.all,
    queryFn: async (): Promise<Course[]> => {
      // Diagnostic: check if auth token is available
      const token = await getAuthToken();
      console.log('[useCourses] Auth token available:', !!token, 'length:', token?.length);

      let query = supabase
        .from('courses')
        .select(COURSE_SELECT)
        .order('title');

      if (courseType) {
        query = query.eq('course_type', courseType);
      }

      const { data, error } = await query;
      if (error) {
        console.error('[useCourses] PostgREST error:', error);
        throw error;
      }
      console.log('[useCourses] Got', data?.length, 'courses, courseType:', courseType);
      return mapCourseData(data || []);
    },
    staleTime: 1000 * 60 * 10,
  });
}

export function useCourse(courseId: string | undefined) {
  return useQuery({
    queryKey: courseKeys.byId(courseId || ''),
    queryFn: async (): Promise<Course | null> => {
      if (!courseId) return null;
      const { data, error } = await supabase
        .from('courses')
        .select(COURSE_SELECT)
        .eq('id', courseId)
        .single();

      if (error) throw error;
      if (!data) return null;
      return mapCourseData([data])[0];
    },
    enabled: !!courseId,
    staleTime: 1000 * 60 * 10,
  });
}
